import React, { useRef } from "react";
import Text from "@/components/ui/text";
import { NextStep } from "@/pages/deals/types";
import { ReservedUserIds } from "@/entities/users/types";
import TextEditorWithActions, {
  TextEditorWithActionsRef
} from "@/components/controls/TextEditor/TextEditorWithActions";
import OptionsDropdownMenu, {
  DetailedViewOptions
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { simpleDate } from "@/utils/datetime";
import AutofillIndication from "@/components/controls/AutofillIndication";
import { cn } from "@/utils/utils";

interface NextStepItemProps {
  nextStep: NextStep;
  allNextSteps: NextStep[];
  onUpdate: (nextStepId: string, newText?: string) => void;
  onDelete: (nextStepId: string) => void;
  showDropdown?: boolean;
  className?: string;
  dateClassName?: string;
}

const NextStepItem: React.FC<NextStepItemProps> = ({
  nextStep,
  allNextSteps,
  onUpdate,
  onDelete,
  showDropdown = true,
  className,
  dateClassName
}) => {
  const textEditorRef = useRef<TextEditorWithActionsRef>(null);

  const getOptions = (): DetailedViewOptions => ({
    edit: () => {
      setTimeout(() => {
        textEditorRef.current?.forceStartEditing();
      }, 100);
    },
    delete: () => onDelete(nextStep.id)
  });

  const isDreamerMode =
    nextStep.lastEditedBy === ReservedUserIds.DREAMER_CONVERSATIONAL_ID ||
    allNextSteps
      .slice(1)
      .some(
        (step) =>
          step.lastEditedBy === ReservedUserIds.DREAMER_CONVERSATIONAL_ID
      );

  return (
    <div
      key={nextStep.id}
      className={cn("flex items-start justify-between min-h-10", className)}
    >
      <div className="flex flex-col w-full gap-2">
        <div className="flex-1 min-w-0">
          <TextEditorWithActions
            ref={textEditorRef}
            onSave={(newValue: string) => {
              onUpdate(nextStep.id, newValue);
            }}
            initialValue={nextStep.description || ""}
            width="100%"
            autoSave={false}
            showActions={true}
          />
        </div>
        <div
          className={cn(
            "flex w-full gap-2 items-center",
            isDreamerMode ? "pl-2" : "pl-4"
          )}
        >
          {isDreamerMode && <AutofillIndication />}
          {nextStep.createdAt && (
            <Text
              variant="smBodySecondary"
              className={cn(dateClassName, "flex-1")}
            >
              {simpleDate(new Date(nextStep.createdAt))}
            </Text>
          )}
        </div>
      </div>

      {showDropdown && (
        <div className="flex-shrink-0">
          <OptionsDropdownMenu
            options={getOptions()}
            tPrefix="deals.nextSteps.options"
            buttonVariant="iconNoBg"
            iconSize="sm"
            iconColor="secondary"
          />
        </div>
      )}
    </div>
  );
};

export default NextStepItem;
